export default {
    /**
     * Start an image segmentation job.
     * @param {number} customerId - The customer ID
     * @param {string} imagePath - Path to the image to segment
     * @param {number|null} layoutId - Optional layout ID if segmenting from existing layout
     * @param {string|null} layoutName - Optional name for new layout
     * @param {number|null} displayedWidth - Actual displayed width of the image in canvas
     * @param {number|null} displayedHeight - Actual displayed height of the image in canvas
     * @param {string|null} segmentationJobUuid - Optional UUID of previous segmentation job for caching
     * @param {boolean} forceReanalyze - Force new analysis, ignoring cache
     * @returns {Promise} Axios response with job UUID
     */
    startSegmentation(customerId, imagePath, layoutId = null, layoutName = null, displayedWidth = null, displayedHeight = null, segmentationJobUuid = null, forceReanalyze = false) {
        const payload = {
            image_path: imagePath,
            layout_id: layoutId,
            layout_name: layoutName,
        };

        // Pass displayed dimensions if available (for accurate template sizing)
        if (displayedWidth && displayedHeight) {
            payload.displayed_width = displayedWidth;
            payload.displayed_height = displayedHeight;
        }

        // Pass segmentation job UUID if template was created from a previous segmentation
        if (segmentationJobUuid) {
            payload.segmentation_job_uuid = segmentationJobUuid;
        }

        // Force re-analysis, ignoring cache
        if (forceReanalyze) {
            payload.force_reanalyze = true;
        }

        return axios.post(`/banner/${customerId}/group/segment-image`, payload);
    },

    /**
     * Get the status of a segmentation job.
     * @param {string} uuid - The job UUID
     * @returns {Promise} Axios response with job status and layer data
     */
    getJobStatus(uuid) {
        return axios.get(`/segmentation-job/${uuid}`);
    },

    /**
     * Rebuild a background layer, optionally steered by a note from the user.
     * @param {string} uuid - The job UUID
     * @param {string} layerId - The layer to rebuild
     * @param {string|null} guidance - Optional free text appended to the prompt
     */
    improveBackground(uuid, layerId, guidance = null) {
        return axios.post(`/segmentation-job/${uuid}/layers/${layerId}/improve-background`, {
            guidance: guidance || null,
        });
    },

    /**
     * Rebuild a product cutout, optionally steered by a note from the user.
     * @param {string} uuid - The job UUID
     * @param {string} layerId - The layer to rebuild
     * @param {string|null} guidance - Optional free text appended to the prompt
     */
    improveProduct(uuid, layerId, guidance = null) {
        return axios.post(`/segmentation-job/${uuid}/layers/${layerId}/improve-product`, {
            guidance: guidance || null,
        });
    },

    /**
     * Create a layout from selected segmentation layers.
     * @param {number} customerId - The customer ID
     * @param {string} jobUuid - The segmentation job UUID
     * @param {string[]} selectedLayerIds - Array of layer IDs to include
     * @param {string|null} layoutName - Optional name for new layout
     * @param {number|null} targetLayoutId - Optional layout ID to add template to (null = create new layout)
     * @param {string[]} ovalShadowLayerIds - Product layers that should receive an oval shadow
     * @returns {Promise} Axios response with new layout data
     */
    createFromSegmentation(customerId, jobUuid, selectedLayerIds, layoutName = null, targetLayoutId = null, convertToTextLayerIds = [], extractionMode = 'product_only', removeBgLayerIds = [], ovalShadowLayerIds = []) {
        const payload = {
            segmentation_job_uuid: jobUuid,
            selected_layer_ids: selectedLayerIds,
            layout_name: layoutName,
        };

        // Only include customer_id if it's a valid value (not 0 or falsy)
        if (customerId && customerId > 0) {
            payload.customer_id = customerId;
        }

        // Pass target_layout_id to explicitly control whether to create new layout or add to existing
        // null = create new layout, number = add to that layout
        if (targetLayoutId !== undefined) {
            payload.target_layout_id = targetLayoutId;
        }

        // Pass layer IDs that should be converted from text_image to editable text
        if (convertToTextLayerIds && convertToTextLayerIds.length > 0) {
            payload.convert_to_text_layer_ids = convertToTextLayerIds;
        }

        // Pass extraction mode ('product_only' or 'full')
        if (extractionMode) {
            payload.extraction_mode = extractionMode;
        }

        // Pass layer IDs that should have their background removed
        if (removeBgLayerIds && removeBgLayerIds.length > 0) {
            payload.remove_bg_layer_ids = removeBgLayerIds;
        }

        if (ovalShadowLayerIds && ovalShadowLayerIds.length > 0) {
            payload.oval_shadow_layer_ids = ovalShadowLayerIds;
        }

        return axios.post(`/banner/${customerId}/group/create-from-segmentation`, payload);
    },

    /**
     * Poll for job completion with timeout.
     * @param {string} uuid - The job UUID
     * @param {number} maxAttempts - Maximum polling attempts (default 60)
     * @param {number} intervalMs - Polling interval in ms (default 3000)
     * @returns {Promise} Resolves with job data when complete, rejects on error or timeout
     */
    pollJobStatus(uuid, maxAttempts = 60, intervalMs = 3000) {
        return new Promise((resolve, reject) => {
            let attempts = 0;

            const poll = async () => {
                try {
                    const response = await this.getJobStatus(uuid);
                    const job = response.data;

                    if (job.status === 'completed') {
                        resolve(job);
                        return;
                    }

                    if (job.status === 'failed') {
                        reject(new Error(job.error_message || 'Segmentation failed'));
                        return;
                    }

                    attempts++;
                    if (attempts >= maxAttempts) {
                        reject(new Error('Segmentation timed out'));
                        return;
                    }

                    setTimeout(poll, intervalMs);
                } catch (error) {
                    reject(error);
                }
            };

            poll();
        });
    },
};
